import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { setFilterFromLocToUserTabs } from "../../redux/slices/users/users";
import {
  setLoadingUserLoc,
  setModalLocations,
  setPreselectedFilterField,
  setShowFilterForm,
  setUserLocationId,
  setUserLocationName,
} from "../../redux/slices/fleet/locations";

import { OrgGeoPointData } from "../../redux/actions/locations/locationsTypes";
import { State } from "../../redux/slices";
import { v4 as uuidv4 } from "uuid";
import { ColumnTables } from "../../redux/slices/userGroups/userTablestyping";

import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShareIcon from "@mui/icons-material/Share";
import {
  setIsLocationShared,
  setLocationSelectedDetails,
} from "../../redux/slices/location/singleLocation";
import { stylesGeneralComponents } from "../StylesMuiGeneralComponents";
import { userLocationsStyles } from "./UserLocationsStyles";
import {
  applyLocalFilter,
  getOperatorsFromAvailableFilters,
} from "../helpers/helpersSearchs";
import CustomGeneralColumnHeader from "../customFilterComponent/customHeader";

const SetActions = () => {
  const dispatch = useDispatch();
  return {
    setLocationId: (id: number, name: string) => {
      dispatch(setUserLocationId(id));
      dispatch(setUserLocationName(name));
      dispatch(setModalLocations(true));
    },

    setLocationSelectedDetails: (location: any) => {
      dispatch(setLocationSelectedDetails(location));
    },

    setIsLocationShared: (value: boolean) => {
      dispatch(setIsLocationShared(value));
    },

    setLocFilter: (location: string, navigate: any) => {
      dispatch(setFilterFromLocToUserTabs(location));
      dispatch(setLoadingUserLoc(true));
      setTimeout(() => {
        dispatch(setLoadingUserLoc(false));
        navigate(`/users`);
      }, 1000);
    },

    onOpenFilter: (field: string) => {
      dispatch(setShowFilterForm(true));
      dispatch(setPreselectedFilterField(field));
    },
  };
};

const UserLocationsColumns = (t: (arg0: string) => string) => {
  const navigate = useNavigate();
  const { setLocationSelectedDetails, setIsLocationShared, onOpenFilter } =
    SetActions();
  const { locations, selectedLocationsToShow, rawColumns, filterModel } =
    useSelector((state: State) => state.LocationsReducer);
  const { locationSelectedDetails } = useSelector(
    (state: State) => state.SingleLocationReducer,
  );

  const locationsToShow = locations.filter((loc) =>
    selectedLocationsToShow.includes(loc.id),
  );

  const columnNames = {
    name: t("Fleet_users_locations.table.Location_name"),
    id: t("Fleet_users_locations.table.Location_inarix_id"),
    longitude: t("Fleet_users_locations.table.Longitude"),
    latitude: t("Fleet_users_locations.table.Latitude"),
    location: t("Common_options.loc"),
    group: t("Users_managers.groups"),
    idParams: "",
    capacity: t("Gran_tracker_bins.capacity-text"),
  };

  let columns: any[] = rawColumns.map((column: ColumnTables) => {
    if (column.field === "name") {
      return {
        disableColumnMenu: true,
        headerName: (columnNames as never)[column.field],
        field: column.field,
        width: column.width,
        sortable: false,
        filterOperators: getOperatorsFromAvailableFilters([
          "$ilike",
          "$eq",
          "$ne",
        ]),
        filter: "LOCAL",
        service: "text",

        renderHeader: () => (
          <CustomGeneralColumnHeader
            headerName={(columnNames as never)[column.field]}
            field={column.field}
            filterModel={filterModel}
            availableFilters={["$ilike", "$eq", "$ne"]}
            onOpenFilter={onOpenFilter}
            backgroundColor="#fff"
          />
        ),
        renderCell: (params: GridRenderCellParams) => {
          return (
            <div className="container-name-table-site">
              {params.row.shared ? (
                <ShareIcon sx={stylesGeneralComponents.shareIcon} />
              ) : (
                ""
              )}
              <div className={params.row.shared ? "ml-2" : ""}>
                {params.value}
              </div>
            </div>
          );
        },
      };
    }

    if (column.field === "group") {
      return {
        headerName: (columnNames as never)[column.field],
        field: column.field,
        width: column.width,
        sortable: false,
        disableColumnMenu: true,
        filterOperators: getOperatorsFromAvailableFilters([
          "$ilike",
          "$eq",
          "$ne",
        ]),
        filter: "LOCAL",
        service: "text",
        renderHeader: () => (
          <CustomGeneralColumnHeader
            headerName={(columnNames as never)[column.field]}
            field={column.field}
            filterModel={filterModel}
            availableFilters={["$ilike", "$eq", "$ne"]}
            onOpenFilter={onOpenFilter}
            backgroundColor="#fff"
          />
        ),
        renderCell: (params: GridRenderCellParams) => {
          const list = params.value;
          if (list.length === 1) {
            return (
              <Box sx={userLocationsStyles.groupChipContainer}>
                {list.map((value: string) => (
                  <Chip variant="outlined" key={value} label={value} />
                ))}
              </Box>
            );
          }

          if (list.length > 1) {
            return (
              <Box sx={userLocationsStyles.groupChipContainer}>
                <Tooltip
                  title={
                    <React.Fragment>
                      <Typography color="inherit">
                        {t("Users_managers.groups")}
                      </Typography>
                      <ul>
                        {list.map((l: string) => (
                          <li key={`${l}${uuidv4()}`}>{l}</li>
                        ))}
                      </ul>
                    </React.Fragment>
                  }
                  placement="right"
                >
                  <Chip
                    variant="outlined"
                    label={`${list[0]}  ${
                      list.length >= 2
                        ? `+ ${list.length - 1} ${t("Common_options.others")}`
                        : ""
                    }  `}
                  />
                </Tooltip>
              </Box>
            );
          }
        },
      };
    }

    if (column.field === "capacity") {
      return {
        headerName: (columnNames as never)[column.field],
        field: column.field,
        width: column.width,
        sortable: false,
        disableColumnMenu: true,
        filterOperators: getOperatorsFromAvailableFilters([
          "$eq",
          "$gt",
          "$lt",
          "$ne",
        ]),
        filter: "LOCAL",
        service: "operator",
        renderHeader: () => (
          <CustomGeneralColumnHeader
            headerName={(columnNames as never)[column.field]}
            field={column.field}
            filterModel={filterModel}
            availableFilters={["$eq", "$gt", "$lt", "$ne"]}
            onOpenFilter={onOpenFilter}
            backgroundColor="#fff"
          />
        ),
        renderCell: (params: GridRenderCellParams) => {
          const formatCapacity = (value: number) => {
            const hasDecimals = value % 1 !== 0;

            if (value >= 1e6) {
              // For values >= 1 million, use scientific notation
              return hasDecimals
                ? value.toExponential(2)
                : value.toExponential(0);
            } else if (value >= 1000) {
              // For values >= 1000, show with comma separators
              return value.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: hasDecimals ? 2 : 0,
              });
            } else {
              // For smaller values
              return hasDecimals ? value.toFixed(2) : value.toString();
            }
          };

          const rawValue = params.row.capacityDisplay?.value;
          const fullCapacity = rawValue ? `${rawValue.toFixed(2)} T` : "";
          const displayCapacity = rawValue
            ? `${formatCapacity(rawValue)} T`
            : "";

          const capacityDisplay =
            rawValue >= 1e6 ? (
              <Tooltip title={fullCapacity} placement="top">
                <span style={userLocationsStyles.capacityTooltipSpan}>
                  {displayCapacity}
                </span>
              </Tooltip>
            ) : (
              <span style={userLocationsStyles.capacitySpan}>
                {displayCapacity}
              </span>
            );

          const details = (
            <IconButton
              onClick={() => {
                navigate(`/site/details/${params.row.name}`);
                setIsLocationShared(params.row.shared);
                setLocationSelectedDetails({
                  ...locationSelectedDetails,
                  id: params.row.id,
                });
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          );
          const options = (
            <IconButton onClick={() => console.log("options")}>
              <MoreVertIcon />
            </IconButton>
          );

          return (
            <div className="options-columns-capacity-locations">
              {capacityDisplay}
              <div>
                {details}
                {/* {options} */}
              </div>
            </div>
          );
        },
      };
    }

    if (column.field === "location") {
      return {
        headerName: (columnNames as never)[column.field],
        field: column.field,
        width: column.width,
        sortable: false,
        disableColumnMenu: true,

        renderHeader: () => (
          <CustomGeneralColumnHeader
            headerName={(columnNames as never)[column.field]}
            field={column.field}
            filterModel={filterModel}
            availableFilters={["$ilike", "$eq", "$ne"]}
            onOpenFilter={onOpenFilter}
            backgroundColor="#fff"
          />
        ),
        filterOperators: getOperatorsFromAvailableFilters([
          "$ilike",
          "$eq",
          "$ne",
        ]),
        filter: "LOCAL",
        service: "text",
        renderCell: (params: GridRenderCellParams) => {
          const handleValues = () => {
            if (params.value) {
              return params.value;
            } else {
              return (
                <div>{`Lat: ${params.row.latitude} , Long: ${params.row.longitude}`}</div>
              );
            }
          };

          return <div>{handleValues()}</div>;
        },
      };
    }

    return {
      field: column.field,
      headerName: (columnNames as never)[column.field]
        ? (columnNames as never)[column.field]
        : column.field,
      width: 50,
      sortable: column.sortable,
    };
  });

  let locationsRows = locationsToShow?.map((locations: OrgGeoPointData) => {
    const rows = {
      name: locations?.name,
      id: locations?.id,
      longitude: locations?.longitude.toString().slice(0, 7),
      latitude: locations?.latitude.toString().slice(0, 7),
      idParams: locations?.id,
      deletedAt: locations.deletedAt,
      createdAt: locations?.createdAt,
      updatedAt: locations?.updatedAt,
      typeId: locations?.typeId,
      primaryContactName: locations?.primaryContactName,
      telephone: locations?.telephone,
      email: locations?.email,
      location: locations?.address,
      notes: locations?.notes,
      remoteId: locations?.remoteId,
      group: locations.locationGroups.map((group) => group.groupName),
      capacity: locations.totalCapacity?.value || 0, // Extract numeric value for filtering
      capacityDisplay: locations.totalCapacity, // Keep original for display
      shared: locations.shared,
      type: {
        id: locations?.type?.id,
        name: locations?.type?.name,
      },
    };

    return rows;
  });

  // Apply LOCAL filters from filterModel
  if (filterModel?.items && filterModel.items.length > 0) {
    locationsRows = locationsRows.filter((locationRow) => {
      return filterModel.items.every((filterItem) => {
        const column = columns.find((col) => col.field === filterItem.field);

        if (!column?.filter || column.filter !== "LOCAL") {
          return true;
        }

        // Enrich filterItem with service from column definition
        const enrichedFilterItem = {
          ...filterItem,
          service: column.service,
        };

        return applyLocalFilter(enrichedFilterItem, locationRow);
      });
    });
  }

  return {
    columns,
    locationsRows,
  };
};

export default UserLocationsColumns;
